import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Clapperboard, Sparkles, Loader2, Cpu, Rocket, ChevronRight, Wand2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/Bits";
import { toast } from "sonner";

const EXAMPLE = "Create an original cinematic sci-fi first-person shooter set in a partially destroyed futuristic city. The player is part of an elite recovery team sent in after communications suddenly stop. Build a playable opening mission with intelligent enemies, weapons, objectives, environmental storytelling and a cinematic introduction.";

const QUALITIES = [
  { key: "prototype", label: "Prototype", note: "Fast, basic mechanics" },
  { key: "standard", label: "Standard", note: "Better graphics & mechanics" },
  { key: "professional", label: "Professional", note: "Commercial-quality systems" },
  { key: "cinematic", label: "Cinematic / AAA", note: "Maximum quality pipeline" },
];

const STATUS_COLOR = {
  building: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  ready: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
};

export default function StudioPro() {
  const navigate = useNavigate();
  const [idea, setIdea] = useState("");
  const [quality, setQuality] = useState("professional");
  const [busy, setBusy] = useState(false);
  const [projects, setProjects] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/gv2/projects"); setProjects(data.projects || []); }
    catch { setProjects([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (idea.trim().length < 4) { toast.error("Describe the game you want to build."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/gv2/projects", { idea: idea.trim(), quality });
      toast.success("Game Director is on it — building your vertical slice!");
      navigate(`/app/studio-pro/${data.project.id}`, { state: { jobId: data.job.id } });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start the build.");
      setBusy(false);
    }
  };

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="studio-pro-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-2xl grid place-items-center bg-gradient-to-br from-cyan-500 to-violet-600 shadow-lg shadow-violet-600/20">
          <Clapperboard className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold leading-none">GameView Studio Pro</h1>
          <p className="text-[hsl(var(--muted-foreground))] text-sm mt-1">The AI production engine — describe a game, the AI workforce builds it.</p>
        </div>
        <Badge variant="outline" className="ml-auto hidden sm:flex gap-1.5 border-violet-500/40 text-violet-400"><Cpu className="w-3.5 h-3.5" /> GameView 2.0</Badge>
      </div>

      {/* New project */}
      <div className="mt-6 rounded-3xl border border-[hsl(var(--border))] bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--card))]/40 p-5 sm:p-6" data-testid="gv2-new-project">
        <label className="text-sm font-semibold flex items-center gap-2 mb-2"><Wand2 className="w-4 h-4 text-violet-400" /> Describe your game</label>
        <textarea
          value={idea} onChange={(e) => setIdea(e.target.value)} rows={4}
          placeholder="e.g. An original cinematic sci-fi FPS in a destroyed future city with intelligent enemy squads, weapons, objectives and a cinematic intro…"
          data-testid="gv2-idea-input"
          className="w-full rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-500/40"
        />
        <button onClick={() => setIdea(EXAMPLE)} data-testid="gv2-example-btn"
          className="text-xs text-violet-400 hover:underline mt-1.5 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Try the sci-fi FPS example</button>

        <div className="mt-4">
          <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))] mb-2">Quality target</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="gv2-quality">
            {QUALITIES.map((q) => (
              <button key={q.key} onClick={() => setQuality(q.key)} data-testid={`gv2-quality-${q.key}`}
                className={`text-left rounded-xl border px-3 py-2.5 transition-colors ${quality === q.key ? "border-violet-500 bg-violet-500/10" : "border-[hsl(var(--border))] hover:border-violet-500/40"}`}>
                <div className="font-semibold text-sm">{q.label}</div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{q.note}</div>
              </button>
            ))}
          </div>
        </div>

        <Button onClick={create} disabled={busy} data-testid="gv2-build-btn"
          className="mt-5 rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 hover:opacity-90 gap-2 h-11 px-6">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Starting the studio…</> : <><Rocket className="w-4 h-4" /> Build my game</>}
        </Button>
        <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2">The Game Director authors the Game DNA, the Production Graph and a playable vertical slice — then you reshape it with plain-English commands.</p>
      </div>

      {/* Existing projects */}
      <h2 className="font-display text-xl font-bold mt-9 mb-3">Your productions</h2>
      {!projects ? <Spinner /> : projects.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] p-8 text-center text-sm text-[hsl(var(--muted-foreground))]" data-testid="gv2-empty">
          No productions yet — describe a game above and the AI workforce will build your first vertical slice.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gv2-projects-grid">
          {projects.map((p) => (
            <button key={p.id} onClick={() => navigate(`/app/studio-pro/${p.id}`)} data-testid={`gv2-project-${p.id}`}
              className="text-left rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 card-hover">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-bold truncate flex-1">{p.name}</span>
                <Badge variant="outline" className={`text-[10px] ${STATUS_COLOR[p.status] || ""}`}>{p.status}</Badge>
              </div>
              <div className="text-xs text-[hsl(var(--muted-foreground))] mb-3">{(p.dna?.identity?.genre) || "Designing…"}</div>
              <div className="h-1.5 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
                <div className="h-full bg-gradient-to-r from-cyan-500 to-violet-600" style={{ width: `${p.build?.percent || 0}%` }} />
              </div>
              <div className="flex items-center justify-between mt-2 text-[11px] text-[hsl(var(--muted-foreground))]">
                <span>{p.build?.total_nodes || 0} nodes · {p.build?.percent || 0}%</span>
                <span className="flex items-center gap-0.5 text-violet-400 font-semibold">Open <ChevronRight className="w-3.5 h-3.5" /></span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
